import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import { QUANTITY_INPUT } from "./quantityInput";

/**
 * Upsert Inventory Policy Limits
 *
 * Writes the LIMITS family of `im_inventory_policies` — min/max quantities —
 * for one policy, shaped after the Gently model rather than any source system.
 * The classification family (stock status, popularity, source, buyer) has its
 * own step (`upsert_inventory_policy_classification`); the two column sets are
 * field-disjoint by design, and a step without the other family's sockets
 * cannot clobber it. See
 * docs/procurement/policy-upsert-single-step-alternative.md for why this is
 * two steps.
 *
 * An existing `calculation_method = 'manual'` always wins — a buyer's manual
 * override survives every sync. Source quirks (e.g. sentinel "no value"
 * markers) must be handled by the mapping upstream: a `minQuantity` arriving
 * here means exactly what it says.
 */
export const upsertInventoryPolicyLimitsStep = defineStepConfig({
  type: "upsert_inventory_policy_limits",
  name: "Upsert Inventory Policy Limits",
  description:
    "Upserts one inventory policy's min/max quantities (warehouse code, product SKU, min, max). Never touches the classification columns — those belong to Upsert Inventory Policy Classification. Manual calculation_method always survives.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    productSku: z.string().describe("Product SKU (resolved to the product)"),
    warehouseCode: z
      .string()
      .describe("Warehouse origin code (resolved to the warehouse)"),
    minQuantity: QUANTITY_INPUT.describe("Policy minimum quantity"),
    maxQuantity: QUANTITY_INPUT.describe("Policy maximum quantity"),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The policy limits were upserted"),
    skipped: z
      .boolean()
      .describe("The row was skipped because a reference was unresolved"),
    unresolvedProduct: z
      .boolean()
      .describe(
        "No product matched the SKU (ingest the product catalog first)",
      ),
    unresolvedWarehouse: z
      .boolean()
      .describe("No warehouse matched the warehouse code"),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    originSystem: z
      .string()
      .optional()
      .describe(
        "Restrict WAREHOUSE resolution to references ingested from this origin system. Does not scope products: ProductV3 is keyed by SKU within the organization and carries no origin system. Leave empty to match any.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
